import { useEffect, useState } from "react";
import { Info } from "lucide-react";

/**
 * Interactive floor plan with hoverable / clickable table pins.
 * Props:
 *   plan: { id, name, image_url, tables: [{ id, label, x, y, capacity, min_spend, category, status, bottle_packages }] }
 *   selectedTableId: string | null
 *   onSelect: (table) => void
 */
export default function FloorPlanMap({ plan, selectedTableId, onSelect }) {
  const [hovered, setHovered] = useState(null);

  if (!plan) {
    return (
      <div className="aspect-square md:aspect-[1/1] bg-[var(--onyx-charcoal)] border border-white/10 flex items-center justify-center text-white/40 font-display italic">
        Loading floor plan…
      </div>
    );
  }

  const active = hovered || plan.tables.find((t) => t.id === selectedTableId);

  return (
    <div className="relative w-full">
      <div
        data-testid={`floorplan-${plan.id}`}
        className="relative w-full overflow-hidden border border-white/10 bg-black"
        style={{ aspectRatio: "1 / 1" }}
      >
        <img
          src={plan.image_url}
          alt={plan.name}
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-black/10" />

        {plan.tables.map((t) => {
          const isSelected = t.id === selectedTableId;
          const isReserved = t.status === "reserved" || t.status === "unavailable";
          const isLimited = t.status === "limited";
          return (
            <button
              key={t.id}
              data-testid={`table-pin-${t.label}`}
              onMouseEnter={() => setHovered(t)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => !isReserved && onSelect && onSelect(t)}
              className={`table-pin ${isSelected ? "is-selected" : ""} ${isReserved ? "is-reserved" : ""} ${isLimited ? "is-limited" : ""}`}
              style={{ left: `${t.x}%`, top: `${t.y}%` }}
              type="button"
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-[10px] tracking-luxe uppercase text-white/50">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full border border-gold bg-black" /> Available</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full border" style={{ borderColor: "#E0A95C" }} /> Limited</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full border border-white/20" /> Reserved</span>
        <span className="flex items-center gap-2 ml-auto"><Info size={12} className="text-gold" /> Hover or tap a table</span>
      </div>

      {/* Active preview card */}
      {active && (
        <div
          data-testid="floorplan-preview"
          className="mt-6 glass p-6 md:p-7 border-l-2 border-gold animate-[fadeUp_400ms_ease-out_both]"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="eyebrow mb-1">Table · {active.section.replace("_", " ")}</p>
              <h4 className="font-display text-3xl text-white">{active.label}</h4>
            </div>
            <span className={`text-[10px] tracking-luxe uppercase px-2 py-1 border ${active.category === "vip" ? "border-gold text-gold" : "border-white/20 text-white/70"}`}>
              {active.category === "vip" ? "VIP" : active.category}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-[10px] tracking-luxe uppercase text-white/40 mb-1">Capacity</p>
              <p className="font-display text-2xl">{active.capacity}</p>
            </div>
            <div>
              <p className="text-[10px] tracking-luxe uppercase text-white/40 mb-1">Min Spend</p>
              <p className="font-display text-2xl text-gold">${active.min_spend.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[10px] tracking-luxe uppercase text-white/40 mb-1">Status</p>
              <p className="font-display text-2xl capitalize">{active.status}</p>
            </div>
          </div>
          {active.bottle_packages?.length > 0 && (
            <div className="mt-5 pt-5 border-t border-white/10">
              <p className="text-[10px] tracking-luxe uppercase text-white/40 mb-2">Bottle Packages</p>
              <div className="flex flex-wrap gap-2">
                {active.bottle_packages.map((b, i) => (
                  <span key={i} className="text-xs text-white/70 border border-white/15 px-2.5 py-1">{b}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
